/**
 * OS Today — Unified Daily Intelligence Page
 * FULLY ADDITIVE — separate route, NOT in main nav, does NOT replace /dashboard
 * Route: /today-os
 */
import React, { useState, useEffect } from 'react';
import BiologicalLayout from '@/components/layout/BiologicalLayout';
import { useAuth, API } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Loader2, Brain, Activity, Shield, AlertCircle, ChevronRight, Zap, Clock } from 'lucide-react';
import axios from 'axios';

const PRIORITY_COLORS = {
  high: '#C85C5C', medium: '#D4A843', low: '#4E7A6B',
};

export default function OSTodayPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const subscriberId = user?.id;

  useEffect(() => {
    if (!subscriberId) return;
    axios.get(`${API}/os/today/${subscriberId}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [subscriberId]);

  if (loading) return (
    <BiologicalLayout><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 text-[#C89B7B] animate-spin" /></div></BiologicalLayout>
  );

  if (!data) return (
    <BiologicalLayout><div className="max-w-2xl mx-auto px-4 py-12 text-center">
      <p className="text-sm text-[#8A8681]">Unable to load today's intelligence</p>
    </div></BiologicalLayout>
  );

  const arrival = data.arrival_state || {};
  const daily = data.daily_state || {};
  const completeness = arrival.data_completeness || {};

  return (
    <BiologicalLayout>
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6" data-testid="os-today-page">
        {/* Header */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A8681] font-['Work_Sans'] mb-2">Care OS</p>
          <h1 className="text-xl sm:text-2xl font-['Outfit'] font-light text-[#2C2A29]">Today's Intelligence</h1>
          <p className="text-xs text-[#8A8681] font-['Work_Sans'] mt-1">{data.date} &middot; Mode: {data.mode}</p>
        </div>

        {/* Top Priority */}
        {data.top_priority && (
          <div className="rounded-2xl border border-[#8C6B55]/15 p-5 bg-[#8C6B55]/[0.02]" data-testid="top-priority">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8C6B55]/50 font-['Work_Sans'] mb-1">Top Priority</p>
            <p className="text-base font-['Outfit'] text-[#2C2A29] capitalize">{data.top_priority.replace(/_/g, ' ')}</p>
          </div>
        )}

        {/* Risk Alerts */}
        {data.risk_alerts?.length > 0 && (
          <div className="space-y-2" data-testid="risk-alerts">
            {data.risk_alerts.map((r, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#D4A843]/[0.06] border border-[#D4A843]/15">
                <AlertCircle className="w-4 h-4 text-[#D4A843]" strokeWidth={1.5} />
                <p className="text-xs text-[#8C6B55] font-['Work_Sans'] flex-1">{r.message}</p>
                <Badge variant="outline" className="text-[9px] border-[#D4A843]/30 text-[#D4A843]">{r.level}</Badge>
              </div>
            ))}
          </div>
        )}

        {/* Arrival State */}
        <div className="rounded-2xl border border-[#EBE8E3] p-5" data-testid="arrival-state">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-3">Biological Arrival State</p>
          <div className="grid grid-cols-3 gap-3 mb-3">
            {[
              { label: 'BAS', data: arrival.BAS, active: completeness.decode },
              { label: 'NAS', data: arrival.NAS, active: completeness.decode },
              { label: 'TAS', data: arrival.TAS, active: completeness.decode },
            ].map(axis => (
              <div key={axis.label} className="p-3 rounded-xl bg-[#F8F6F3]">
                <p className="text-[9px] uppercase tracking-wider text-[#8A8681] mb-1">{axis.label}</p>
                {axis.active ? (
                  <div className="space-y-0.5">
                    {Object.entries(axis.data || {}).filter(([_, v]) => v).map(([k, v]) => (
                      <p key={k} className="text-[10px] text-[#5C5955] font-['Work_Sans'] truncate" title={typeof v === 'string' ? v : String(v)}>
                        {typeof v === 'boolean' ? (v ? k.replace(/_/g, ' ') : null) : (typeof v === 'string' ? v.slice(0, 25) : null)}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-[10px] text-[#8A8681] italic">No DECODE data</p>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 text-[10px] text-[#8A8681] font-['Work_Sans']">
            {arrival.severity_score != null && <span>Severity: <strong className="text-[#2C2A29]">{arrival.severity_score}</strong></span>}
            {arrival.stability_score != null && <span>Stability: <strong className="text-[#2C2A29]">{arrival.stability_score}%</strong></span>}
            {arrival.risk_level && <span>Risk: <strong style={{ color: arrival.risk_level === 'high' ? '#C85C5C' : arrival.risk_level === 'rising' ? '#C87B7B' : '#D4A843' }}>{arrival.risk_level}</strong></span>}
          </div>
        </div>

        {/* Daily State */}
        {daily.has_checkin && (
          <div className="rounded-2xl border border-[#EBE8E3] p-5" data-testid="daily-state">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-2">Today's Operational State</p>
            <div className="flex items-center gap-3">
              {daily.current_state && <Badge variant="outline" className="text-xs capitalize">{daily.current_state.replace(/_/g, ' ')}</Badge>}
              {daily.current_pattern && <span className="text-xs text-[#8C6B55] font-['Work_Sans'] capitalize">{daily.current_pattern.replace(/_/g, ' ')}</span>}
            </div>
          </div>
        )}

        {/* Recommended Actions */}
        {data.recommended_actions?.length > 0 && (
          <div className="space-y-2" data-testid="recommended-actions">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans']">Recommended Actions</p>
            {data.recommended_actions.map((a, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[#EBE8E3] hover:bg-[#F8F6F3] transition-colors">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: PRIORITY_COLORS[a.priority] || '#8A8681' }} />
                <p className="text-sm text-[#2C2A29] font-['Work_Sans'] flex-1">{a.action}</p>
                {a.detail && <span className="text-xs text-[#8A8681]">{a.detail}</span>}
                <ChevronRight className="w-3.5 h-3.5 text-[#8A8681]" />
              </div>
            ))}
          </div>
        )}

        {/* Protocol Summary */}
        {data.protocol_summary && (
          <div className="rounded-xl border border-[#EBE8E3] p-4 flex items-center gap-4" data-testid="protocol-summary">
            <Activity className="w-5 h-5 text-[#5C6B8A]" strokeWidth={1.25} />
            <div className="flex-1">
              <p className="text-sm text-[#2C2A29] font-['Work_Sans']">Today's Protocol</p>
              <p className="text-[10px] text-[#8A8681]">{data.protocol_summary.completed_today} of {data.protocol_summary.total_items} completed</p>
            </div>
            <div className="w-12 h-2 bg-[#EBE8E3] rounded-full overflow-hidden">
              <div className="h-full bg-[#5C6B8A] rounded-full" style={{ width: `${data.protocol_summary.total_items ? (data.protocol_summary.completed_today / data.protocol_summary.total_items * 100) : 0}%` }} />
            </div>
          </div>
        )}

        {/* Data Completeness */}
        <div className="flex items-center gap-3 pt-2 text-[9px] text-[#8A8681] font-['Work_Sans']">
          <span>Data sources:</span>
          {['decode', 'labs', 'context'].map(src => (
            <span key={src} className={completeness[src] ? 'text-[#4E7A6B]' : 'text-[#C87B7B]'}>
              {src} {completeness[src] ? '✓' : '✗'}
            </span>
          ))}
        </div>
      </div>
    </BiologicalLayout>
  );
}
